import getIpLocation from './isomorphic-get-ip-location';
import request from '../../../utils/make-api-request';
import { getJwt } from '../../user-provider';
import { Member } from '../../../backend/db/all-entities';
import { pointToLatLng } from '../../../utils/geolocation-format-conversion';
import { getSavedGeolocation } from '../../../utils/saved-geolocation';

export type LocationInputMethod = {
  label: string;
} & (
  | {
      input: {
        placeholder: string;
      };
      getLocation(inputValue: string): Promise<google.maps.LatLngLiteral>;
    }
  | {
      /**
       * If eager is true, getLocation() will be called upon switching input methods and its result
       * will be cached, and getLocation() will not be called when clicking the OK button.
       */
      eager: boolean;
      requiresUserInteraction: boolean;
      getLocation(): Promise<google.maps.LatLngLiteral>;
    }
);

export const savedGeolocationMethod: LocationInputMethod = {
  label: 'Saved location',
  eager: true,
  requiresUserInteraction: false,
  async getLocation() {
    const geolocation = getSavedGeolocation();
    if (geolocation == null) {
      throw new Error(`You haven't set a primary location.`);
    }
    return geolocation;
  }
};

export const preciseLocationMethod: LocationInputMethod = {
  label: 'Precise location',
  eager: true,
  requiresUserInteraction: true,
  getLocation() {
    return new Promise<google.maps.LatLngLiteral>((res, rej) => {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          res({ lat: coords.latitude, lng: coords.longitude });
        },
        error =>
          rej(
            new Error(
              error.code === error.PERMISSION_DENIED
                ? 'Permission to access location was denied.'
                : error.code === error.POSITION_UNAVAILABLE
                ? 'Precise location is unavailable.'
                : 'Failed to access precise location.'
            )
          ),
        {
          enableHighAccuracy: true,
          timeout: 10_000,
          maximumAge: 60_000
        }
      );
    });
  }
};

export const geocodingMethod: LocationInputMethod = {
  label: 'City/Zip',
  input: {
    placeholder: 'Zip code or city, state'
  },
  async getLocation(inputValue) {
    if (inputValue.trim() === '') {
      throw new Error('Please enter a city or zip code');
    }
    const inputValueIsZipCode = !Number.isNaN(parseInt(inputValue));
    const location: google.maps.LatLngLiteral = await request(
      'geocode',
      'get',
      inputValueIsZipCode ? { zip: parseInt(inputValue).toString() } : { query: inputValue }
    );
    return location;
  }
};

export const ipLocationMethod: LocationInputMethod = {
  label: 'IP location',
  eager: true,
  requiresUserInteraction: false,
  getLocation: getIpLocation
};
